import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const Stars = ({ value, size = 15, className = "" }) => (
  <span className={`inline-flex gap-0.5 ${className}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} strokeWidth={1.5}
        className={i <= Math.round(value) ? "text-[#8F6B3A] fill-[#8F6B3A]" : "text-[#8F6B3A]/30"} />
    ))}
  </span>
);

export const ReviewSummary = ({ slug }) => {
  const { data } = useQuery({ queryKey: ["reviews", slug], queryFn: async () => (await api.get(`/reviews/${slug}`)).data });
  if (!data || data.count === 0) return null;
  return (
    <a href="#reviews" data-testid="review-summary" className="inline-flex items-center gap-2 mt-4">
      <Stars value={data.average} />
      <span className="text-sm text-[#5A4C3C]">{data.average} · {data.count} review{data.count === 1 ? "" : "s"}</span>
    </a>
  );
};

export const PostPurchaseReview = ({ slugs, buyerName }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("stars");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [chosen, setChosen] = useState(slugs[0] || "");
  const [name, setName] = useState(buyerName || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { data: fragrances = [] } = useQuery({ queryKey: ["fragrances"], queryFn: async () => (await api.get("/fragrances")).data });
  const nameOf = (s) => fragrances.find((f) => f.slug === s)?.name || s;

  useEffect(() => {
    if (slugs.length > 0) {
      const t = setTimeout(() => setOpen(true), 1400);
      return () => clearTimeout(t);
    }
  }, [slugs]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please tell us your name."); return; }
    setSending(true);
    try {
      await api.post("/reviews", { fragrance_slug: chosen, name, rating, text });
      toast.success("Thank you — your review will appear once approved by the house.");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your review.");
    }
    setSending(false);
  };

  if (slugs.length === 0) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="post-purchase-review-dialog" className="bg-[#DDD3C1] border-[#8F6B3A]/40 text-[#2B2118] max-w-md p-10">
        {step === "stars" ? (
          <div className="text-center">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#8F6B3A]">Bonheur Eternal</p>
            <h3 className="font-display text-2xl mt-3">How would you rate {slugs.length > 1 ? "your purchase" : nameOf(chosen)}?</h3>
            {slugs.length > 1 && (
              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {slugs.map((s) => (
                  <button key={s} data-testid={`ppr-frag-${s}`} onClick={() => setChosen(s)}
                    className={`px-4 py-1.5 text-[11px] tracking-[0.15em] uppercase border transition-colors ${chosen === s ? "border-[#8F6B3A] text-[#8F6B3A] bg-[#8F6B3A]/10" : "border-[#2B2118]/20 text-[#5A4C3C]"}`}>
                    {nameOf(s)}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-center gap-1.5 mt-7" data-testid="ppr-star-picker">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" data-testid={`ppr-star-${i}`}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)}>
                  <Star size={36} strokeWidth={1.25} className={`transition-colors ${i <= (hover || rating) ? "text-[#8F6B3A] fill-[#8F6B3A]" : "text-[#8F6B3A]/30"}`} />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <button data-testid="ppr-write-review-btn" onClick={() => setStep("write")}
                className="mt-8 w-full rounded-full bg-[#8F6B3A] text-[#FBF8F1] py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors">
                Please Write Your Review Now
              </button>
            )}
            <button data-testid="ppr-skip-btn" onClick={() => setOpen(false)} className="mt-4 text-[11px] tracking-[0.15em] uppercase text-[#6E6151] hover:text-[#8F6B3A]">
              Maybe later
            </button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3">
              <Stars value={rating} size={18} />
              <p className="font-display text-xl">{nameOf(chosen)}</p>
            </div>
            <input data-testid="ppr-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
              className="mt-6 w-full bg-transparent border-b border-[#2B2118]/25 pb-2 text-sm outline-none focus:border-[#8F6B3A] placeholder:text-[#6E6151]" />
            <textarea data-testid="ppr-text-input" autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={5}
              placeholder="How does it open, how long does it last, where will you wear it…"
              className="mt-5 w-full bg-transparent border border-[#2B2118]/25 p-3 text-sm outline-none focus:border-[#8F6B3A] placeholder:text-[#6E6151]" />
            <button data-testid="ppr-submit-btn" onClick={submit} disabled={sending}
              className="mt-6 w-full rounded-full bg-[#8F6B3A] text-[#FBF8F1] py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Submit review"}
            </button>
            <p className="mt-4 text-[11px] text-[#6E6151] text-center">Reviews appear after a quick check by the house.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ReviewsSection = ({ slug, fragranceName }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["reviews", slug], queryFn: async () => (await api.get(`/reviews/${slug}`)).data });
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please choose a star rating."); return; }
    if (!name.trim()) { toast.error("Please tell us your name."); return; }
    setSending(true);
    try {
      await api.post("/reviews", { fragrance_slug: slug, name, rating, text });
      toast.success("Thank you — your review will appear once approved by the house.");
      setName(""); setRating(0); setText("");
      qc.invalidateQueries({ queryKey: ["reviews", slug] });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your review.");
    }
    setSending(false);
  };

  return (
    <section id="reviews" className="max-w-7xl mx-auto px-5 md:px-8 py-24 border-t border-[#2B2118]/[0.06]">
      <Reveal>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <h2 className="font-display text-3xl text-[#2B2118] tracking-tight">What wearers say</h2>
            {data?.count > 0 && (
              <div className="flex items-center gap-3 mt-3">
                <Stars value={data.average} size={18} />
                <span className="text-sm text-[#5A4C3C]" data-testid="reviews-average">{data.average} out of 5 — {data.count} review{data.count === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>
        </div>
      </Reveal>
      <div className="grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-3">
          {(!data || data.count === 0) ? (
            <p className="text-[#6E6151]" data-testid="reviews-empty">No reviews yet — be the first to describe how {fragranceName} wears on you.</p>
          ) : (
            <div className="divide-y divide-[#2B2118]/[0.06]" data-testid="reviews-list">
              {data.reviews.map((r) => (
                <div key={r.id} className="py-7" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Stars value={r.rating} />
                    <p className="font-display text-lg text-[#2B2118]">{r.name}</p>
                    <p className="text-xs text-[#6E6151]">{(r.created_at || "").slice(0, 10)}</p>
                  </div>
                  {r.text && <p className="mt-3 text-[#5A4C3C] leading-relaxed">{r.text}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="lg:col-span-2 border border-[#2B2118]/[0.08] bg-[#DDD3C1]/60 p-8 h-fit">
          <p className="font-display text-xl text-[#2B2118]">Write a review</p>
          <div className="flex gap-1 mt-5" data-testid="review-star-picker">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" data-testid={`review-star-${i}`}
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)}
                className="p-0.5">
                <Star size={26} strokeWidth={1.5} className={i <= (hover || rating) ? "text-[#8F6B3A] fill-[#8F6B3A]" : "text-[#8F6B3A]/30"} />
              </button>
            ))}
          </div>
          <input data-testid="review-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
            className="mt-6 w-full bg-transparent border-b border-[#2B2118]/20 pb-2 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118] placeholder:text-[#6E6151]" />
          <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="How does it open, how long does it last, where do you wear it…"
            className="mt-5 w-full bg-transparent border border-[#2B2118]/20 p-3 text-sm outline-none focus:border-[#8F6B3A] text-[#2B2118] placeholder:text-[#6E6151]" />
          <button data-testid="review-submit-btn" type="submit" disabled={sending}
            className="mt-6 w-full rounded-full bg-[#8F6B3A] text-[#FBF8F1] py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors disabled:opacity-60">
            {sending ? "Sending…" : "Submit review"}
          </button>
          <p className="mt-4 text-[11px] text-[#6E6151] text-center">Reviews appear after a quick check by the house.</p>
        </form>
      </div>
    </section>
  );
};
